import axios from "./_axios";
import { API } from "./apiEndPoints";
import { refreshAccountCounts } from "@/utils/accountCountUtils";
import { fetchAccountDetails } from "./appsAPI";
import qs from "qs";

function repeatQueryParamsSerializer(params) {
  return qs.stringify(params, { arrayFormat: "repeat" });
}

function serializeResponsibilityListParams(params) {
  const parts = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (Array.isArray(value)) {
      value.forEach((v) => {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
      });
    } else if (value !== undefined && value !== null) {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });
  return parts.join("&");
}

export const getListofEmployeesV2 = async ({ account_id, queryParamsObj }) => {
  const result = await axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/`,
    params: { ...queryParamsObj },
    paramsSerializer: repeatQueryParamsSerializer,
  });
  return result;
};

export const getEmployeeByIdV2 = async ({ account_id, employeeId }) => {
  const result = await axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${employeeId}/`,
    params: null,
    paramsSerializer: repeatQueryParamsSerializer,
  });
  return result;
};

// The v1 employee payload answers `vacancy` for the requested period (ER-12768).
export const getEmployeeVacancyForPeriod = async ({
  account_id,
  employeeId,
  effectiveFrom,
  effectiveTo,
}) => {
  const result = await axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V1}${account_id}/employee/${employeeId}/`,
    params: {
      effective_from: effectiveFrom || undefined,
      effective_to: effectiveTo || undefined,
    },
  });
  return result;
};

export const getListofEmployee = async (account_id, page_no, size) => {
  let result = await axios()({
    method: "get",
    url: `${API.ACCOUNTList}${account_id}/employee/`,
    params: { page: page_no, size: size },
  });
  return result;
};

export const getListofEmployeeV2 = async (account_id, limit, offset, searchQuery = "") => {
  const params = { limit: limit, offset: offset };

  // Add search parameter if provided
  if (searchQuery && searchQuery.trim() !== "") {
    params.name = searchQuery.trim();
  }

  let result = await axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/`,
    params: params,
  });
  return result;
};

export const getSingleEmployeeV2 = async (account_id, employee_id) => {
  let result = await axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${employee_id}/`,
    params: "",
  });
  return result;
};

/** True when the first argument is an options object (not legacy positional args). */
function isEmployeeApiOptionsArg(firstArg) {
  return (
    firstArg != null &&
    typeof firstArg === "object" &&
    !Array.isArray(firstArg) &&
    Object.hasOwn(firstArg, "account_id")
  );
}

function parseListofEmployeeV1Args(args) {
  if (isEmployeeApiOptionsArg(args[0])) {
    const {
      account_id,
      limit,
      offset,
      searchQuery = "",
      status = "active",
      ordering = "first_name",
      archived = false,
      has_access = undefined,
    } = args[0];
    return { account_id, limit, offset, searchQuery, status, ordering, archived, has_access };
  }

  const [
    account_id,
    limit,
    offset,
    searchQuery = "",
    status = "active",
    ordering = "first_name",
    archived = false,
    has_access = undefined,
  ] = args;

  return { account_id, limit, offset, searchQuery, status, ordering, archived, has_access };
}

async function requestListofEmployeeV1({
  account_id,
  limit,
  offset,
  searchQuery,
  status,
  ordering,
  archived,
  has_access,
}) {
  const params = { limit, offset };

  const trimmedSearch = searchQuery?.trim();
  if (trimmedSearch) {
    params.name = trimmedSearch;
  }

  if (status) {
    params.status = status;
  }

  if (archived) {
    params.archived = archived;
  }

  if (has_access !== undefined) {
    params.has_access = has_access;
  }

  if (ordering) {
    params.ordering = ordering;
  }

  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/`,
    params,
  });
}

/** Supports legacy positional args or a single options object (max 7 params rule: uses rest). */
export const getListofEmployeeV1 = async (...args) => {
  return requestListofEmployeeV1(parseListofEmployeeV1Args(args));
};

export const createEmployee = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/`,
    data: data,
  });
  return result;
};

export const activateEmployee = async (account_id, employee_id) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${employee_id}/activate/`,
  });
  await refreshAccountCounts(account_id);
  return result;
};

export const updateEmployee = (account_id, employee_id, data) => {
  let result = axios()({
    method: "put",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${employee_id}/`,
    data: data,
  });
  return result;
};

export const employeeStateUpdate = (account_id, user_id, data) => {
  let result = axios()({
    method: "put",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${user_id}/`,
    data: data,
  });
  return result;
};

export const terminateEmployee = async (account_id, user_id, payload) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${user_id}/terminate/`,
    data: payload,
  });
  await refreshAccountCounts(account_id);
  return result;
};

export const reactivateEmployee = async (account_id, employee_id) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${employee_id}/reactivate/`,
  });
  await refreshAccountCounts(account_id);
  return result;
};

export const deleteEmployee = (account_id, user_id) => {
  let result = axios()({
    method: "delete",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${user_id}/`,
  });
  return result;
};

export const getEmployeeById = (account_id, emp_id) => {
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${emp_id}/`,
  });
  return result;
};

export const getEmployeeInspectionData = (account_id, emp_id, responsibility_id) => {
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${emp_id}/`,
    params: {
      can_inspect: true,
      responsibility_id: responsibility_id,
    },
  });
  return result;
};

export const getAccountData = async (account_id, options) => {
  return fetchAccountDetails(account_id, options);
};
export const updateEmployeeExcludeValue = (account_id, emp_id, data) => {
  let result = axios()({
    method: "patch",
    url: `${API.ACCOUNTList}${account_id}/employee/${emp_id}/`,
    data: data,
  });
  return result;
};

export const getEmployeeChatter = (account_id, emp_id, page = 1) => {
  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY}${account_id}/employee/${emp_id}/chatter/`,
    params: { page: page },
  });
  return result;
};
export const getEmployeeAssignment = (account_id, emp_id, val) => {
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNTList}${account_id}/employee/${emp_id}/assignments/`,
    params: { path_to_readiness: val },
  });
  return result;
};
export const getEmployeeArchitectResponsibilities = (
  account_id,
  limit = 10,
  offset = 0,
  searchQuery = "",
  archived = false,
  ordering = "name"
) => {
  const params = {
    mastered: "true",
    limit: limit,
    offset: offset,
    archived: archived,
    ordering: ordering,
  };

  // Add name parameter if provided
  if (searchQuery && searchQuery.trim() !== "") {
    params.name = searchQuery.trim();
  }

  // Note: Filters are now handled on the frontend, not sent to backend

  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/`,
    params: params,
    paramsSerializer: serializeResponsibilityListParams,
  });
  return result;
};
export const getEmployeeCoachResponsibilites = (account_id, emp_id) => {
  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY}${account_id}/employee/${emp_id}/employees-to-coach/`,
  });
  return result;
};
export const getEmployeeCoachResponsibilitesV2 = (
  account_id,
  limit = 10,
  offset = 0,
  searchQuery = "",
  archived = false,
  ordering = "name"
) => {
  const params = {
    can_coach: "True",
    limit: limit,
    offset: offset,
    archived: archived,
    ordering: ordering,
  };

  // Add name parameter if provided
  if (searchQuery && searchQuery.trim() !== "") {
    params.name = searchQuery.trim();
  }

  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/`,
    params: params,
  });
  return result;
};
export const getDirectoryEmployees = (account_id) => {
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNTList}${account_id}/employee/readiness-badges/`,
  });
  return result;
};

export const getDirectoryEmployeesV2 = (
  account_id,
  { limit = 20, offset = 0, status = "active", name = "" } = {}
) => {
  const params = { limit, offset };
  if (status) params.status = status;
  if (name && name.trim() !== "") params.name = name.trim();
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/`,
    params,
  });
  return result;
};

export const getDirectoryResponsibilities = (account_id) => {
  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY}${account_id}/responsibilities-directory/`,
  });
  return result;
};

export const getDirectoryResponsibilitiesV2 = (
  account_id,
  { limit = 10, offset = 0, status = "active", name = "" } = {}
) => {
  const params = {
    master_will_coach: true,
    include_badge_holders: true,
    limit,
    offset,
  };
  if (status) params.status = status;
  if (name && name.trim() !== "") params.name = name.trim();
  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/`,
    params,
  });
  return result;
};
export const getEmployeeBadgesList = (account_id, emp_id) => {
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNTList}${account_id}/employee/employee-badges-detail/${emp_id}/`,
  });
  return result;
};

const badgeListParamsSerializer = repeatQueryParamsSerializer;

function parseEmployeeBadgeLevelList(level, levels) {
  if (Array.isArray(level)) {
    return level;
  }
  if (typeof levels === "string" && levels.trim()) {
    return levels
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

function setEmployeeBadgeStatusParam(params, status, isCurrent) {
  if (status != null && String(status).trim() !== "") {
    params.status = String(status).trim().toLowerCase();
    return;
  }
  if (typeof isCurrent === "boolean") {
    params.status = isCurrent ? "current" : "removed";
  }
}

function setEmployeeBadgeStringParamIfPresent(params, key, value) {
  if (value == null || String(value).trim() === "") {
    return;
  }
  params[key] = String(value).trim();
}

function setEmployeeBadgesRequestParamsFromQuery(params, q) {
  setEmployeeBadgeStatusParam(params, q.status, q.is_current);
  setEmployeeBadgeStringParamIfPresent(params, "search", q.search);
  const responsibilityId = q.responsibility_id ?? q.responsibility;
  if (responsibilityId != null && String(responsibilityId).trim() !== "") {
    params.responsibility_id = String(responsibilityId).trim();
  }
  if (q.limit != null) {
    params.limit = q.limit;
  }
  if (q.offset != null) {
    params.offset = q.offset;
  }
  const levelList = parseEmployeeBadgeLevelList(q.level, q.levels);
  if (levelList.length > 0) {
    params.level = levelList;
  }
  setEmployeeBadgeStringParamIfPresent(params, "ordering", q.ordering);
  if (q.include_level_counts === true) {
    params.include_level_counts = true;
  }
  setEmployeeBadgeStringParamIfPresent(params, "expiration_date", q.expiration_date);
}

/**
 * GET .../api/proficiency/v1/account/{account_id}/employee/{emp_id}/badge/
 * Optional query (proficiency API):
 * - limit, offset — pagination
 * - search — text search (e.g. responsibility name)
 * - status — "current" | "removed" (current vs historical badges); omit for no status filter
 *   Legacy: is_current boolean is still accepted and mapped to status.
 * - responsibility_id — filter to badges issued for that responsibility
 * - level — array of proficiency levels (Apprentice | Professional | Coach | Master)
 * - ordering — e.g. "-issued_on"
 * - include_level_counts — when true, response may include level_counts for filter bar
 * - expiration_date — optional query passthrough; UI filters "about to expire" client-side via badge.expiration_date
 *
 * Query string order (for stable URLs): status, search, limit, offset, then level, ordering, include_level_counts, expiration_date.
 */
export const getEmployeeBadges = (account_id, emp_id, query = {}) => {
  const config = {
    method: "get",
    url: `${API.PROFICIENCY_V1}${account_id}/employee/${emp_id}/badge/`,
  };
  const params = {};
  setEmployeeBadgesRequestParamsFromQuery(params, query);
  if (Object.keys(params).length > 0) {
    config.params = params;
    config.paramsSerializer = badgeListParamsSerializer;
  }
  return axios()(config);
};

/**
 * Loads every badge row for an employee by following paginated responses (limit/offset / next).
 * Use this wherever the UI needs the full history, not only the first page.
 */
export const getAllEmployeeBadges = async (account_id, emp_id, query = {}) => {
  let offset = 0;
  const allResults = [];
  let totalCount = null;
  let continueFetching = true;

  while (continueFetching) {
    const { data } = await getEmployeeBadges(account_id, emp_id, {
      limit: 100,
      offset,
      ...query,
    });
    if (!data) {
      continueFetching = false;
      continue;
    }
    if (totalCount === null && typeof data.count === "number") {
      totalCount = data.count;
    }
    const batch = Array.isArray(data.results) ? data.results : [];
    allResults.push(...batch);
    const gotFullCount = typeof totalCount === "number" && allResults.length >= totalCount;
    if (batch.length === 0 || !data.next || gotFullCount) {
      continueFetching = false;
    } else {
      offset += batch.length;
    }
  }

  return {
    count: totalCount ?? allResults.length,
    next: null,
    previous: null,
    results: allResults,
  };
};

export const removePositionAssignmentForEmployee = (account_id, assignment_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/position-assignment/${assignment_id}/relieve/`,
    data: data,
  });
  return result;
};
export const archiveEmployee = async (account_id, empId) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/employee/${empId}/archive/`,
  });
  await refreshAccountCounts(account_id);
  return result;
};
export const unArchiveEmployee = async (account_id, empId) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/employee/${empId}/unarchive/`,
  });
  await refreshAccountCounts(account_id);
  return result;
};
const RECALC_POLL_INTERVAL_MS = 2000;
const RECALC_POLL_TIMEOUT_MS = 120000;

export const normalizeRecalculateReadinessResponse = (data) => {
  if (!data) return null;
  if (data.employee && typeof data.employee === "object") {
    return { ...data.employee, message: data.message };
  }
  return data;
};

export const isReadinessRecalcInProgress = (data) => {
  const normalized = normalizeRecalculateReadinessResponse(data);
  if (!normalized) return false;

  const status = String(normalized.readiness_status || "").toLowerCase();
  if (status === "recals_in_progress" || status === "recalc in progress") {
    return true;
  }

  return normalized.needs_recalc === true && normalized.current_readiness == null;
};

export const recalculateEmployeeReadiness = async (account_id, emp_id) => {
  const url = `${API.RESPONSIBILITY}${account_id}/employee/${emp_id}/recalculate-readiness/`;
  const request = () =>
    axios()({
      method: "get",
      url,
    });

  try {
    const start = Date.now();
    let response = await request();

    while (
      Date.now() - start < RECALC_POLL_TIMEOUT_MS &&
      isReadinessRecalcInProgress(response?.data)
    ) {
      await new Promise((resolve) => setTimeout(resolve, RECALC_POLL_INTERVAL_MS));
      response = await request();
    }

    if (response?.data) {
      response = {
        ...response,
        data: normalizeRecalculateReadinessResponse(response.data),
      };
    }

    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const updateImportanceValue = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/assignment/change-importance/`,
    data: data,
  });
  return result;
};
export const updateEmployeeReadiness = (account_id, employee_id) => {
  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY}${account_id}/employee/${employee_id}/recalculate-readiness/`,
  });
  return result;
};
export const getEmployeeReadinessRecalcStatus = (account_id, employee_id) =>
  axios()({
    method: "get",
    url: `${API.RESPONSIBILITY}${account_id}/employee/${employee_id}/readiness-recalc-status/`,
  });
export const updateAllEmployeeReadiness = (account_id, employee_ids) => {
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY}${account_id}/recalculate-all-readiness/`,
    data: employee_ids,
  });
  return result;
};
// ============================|V2 End points|============================//
export const getEmployeeAssignments = (
  account_id,
  employee_id,
  limit = 10,
  offset = 0,
  status = null,
  search = null
) => {
  if (employee_id == null || employee_id === "") {
    return Promise.reject(new Error("employee_id is required"));
  }
  let url = `${API.ACCOUNT_LIST_V2}${account_id}/position-assignment/?employee=${employee_id}&limit=${limit}&offset=${offset}`;

  // Handle multiple status filters
  if (status) {
    if (Array.isArray(status)) {
      status.forEach((s) => {
        url += `&status=${s}`;
      });
    } else {
      url += `&status=${status}`;
    }
  }

  // Handle search parameter
  if (search) {
    url += `&name=${encodeURIComponent(search)}`;
  }

  let result = axios()({
    method: "get",
    url: url,
  });
  return result;
};

export const getAuthUserResponsibilitiesAssignments = (
  account_id,
  employee_id,
  offset = 0,
  limit = 10,
  searchQuery = "",
  proficiencyLevels = []
) => {
  if (employee_id == null || employee_id === "") {
    return Promise.reject(
      new Error("employee_id is required for responsibility-assignment requests")
    );
  }
  const params = {
    offset,
    limit,
    employee: employee_id,
    status: "active",
    ordering: "-rank",
  };

  // Add name parameter if provided
  if (searchQuery && searchQuery.trim() !== "") {
    params.name = searchQuery.trim();
  }

  // Add proficiency_level parameters (can be multiple for duplicate query params)
  if (proficiencyLevels && proficiencyLevels.length > 0) {
    params.proficiency_level = proficiencyLevels;
  }

  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility-assignment/`,
    params: params,
    paramsSerializer: repeatQueryParamsSerializer,
  });
  return result;
};

export const getAuthUserProficiencyLevel = async (account_id, emp_id) => {
  let result = await axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${emp_id}/proficiency/`,
  });
  return result;
};

export const getReadinessEvolution = async (account_id, emp_id, period) => {
  let result = await axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${emp_id}/readiness-evolution/`,
    params: { period },
  });
  return result;
};

export const getEmployeeEvaluation = async (
  account_id,
  responsibility_id,
  employee_id,
  offset = 0,
  limit = 10,
  queryParams = {}
) => {
  let result = await axios()({
    method: "get",
    url: `${API.PROFICIENCY_V2}${account_id}/evaluation/`,
    params: {
      responsibility: responsibility_id,
      employee: employee_id,
      offset: offset,
      limit: limit,
      ...queryParams,
    },
  });
  return result;
};

export const getEvaluationSummaryByLevel = async (
  account_id,
  responsibility_id,
  employee_id,
  queryParams = {}
) => {
  let result = await axios()({
    method: "get",
    url: `${API.PROFICIENCY_V2}${account_id}/evaluation/summary-by-level/`,
    params: {
      responsibility: responsibility_id,
      employee: employee_id,
      ...queryParams,
    },
  });
  return result;
};

function parseEmployeesToInspectArgs(args) {
  if (isEmployeeApiOptionsArg(args[0])) {
    const {
      account_id,
      limit = 10,
      offset = 0,
      searchQuery = "",
      archived = false,
      ordering = "name",
      badgeFilter = null,
      searchFilters = null,
    } = args[0];
    return {
      account_id,
      limit,
      offset,
      searchQuery,
      archived,
      ordering,
      badgeFilter,
      searchFilters,
    };
  }

  const [
    account_id,
    limit = 10,
    offset = 0,
    searchQuery = "",
    archived = false,
    ordering = "name",
    badgeFilter = null,
    searchFilters = null,
  ] = args;

  return {
    account_id,
    limit,
    offset,
    searchQuery,
    archived,
    ordering,
    badgeFilter,
    searchFilters,
  };
}

function requestEmployeesToInspect({
  account_id,
  limit,
  offset,
  searchQuery,
  archived,
  ordering,
  badgeFilter,
  searchFilters,
}) {
  const params = {
    can_inspect: "true",
    limit,
    offset,
    archived,
    ordering,
  };

  const empTerm = searchFilters?.employee_name?.trim?.() || "";
  const respTerm = searchFilters?.responsibility_name?.trim?.() || "";
  const hasStructuredFilters = empTerm !== "" || respTerm !== "";

  if (hasStructuredFilters) {
    if (empTerm) params.search = empTerm;
    if (respTerm) params.responsibility = respTerm;
  } else {
    const trimmedSearch = searchQuery?.trim();
    if (trimmedSearch) {
      params.search = trimmedSearch;
    }
  }

  if (badgeFilter?.length > 0) {
    params.badge_filter = badgeFilter.map((f) => f.toLowerCase()).join(",");
  }

  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/`,
    params,
  });
}

/** Supports legacy positional args or a single options object (max 7 params rule: uses rest). */
export const getEmployeesToInspect = (...args) => {
  return requestEmployeesToInspect(parseEmployeesToInspectArgs(args));
};

export const getEmployeeCoachCounts = async (account_id, queryParams = {}) => {
  let result = await axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/employee-to-coach-badge-count/`,
    params: queryParams,
  });
  return result;
};

const PAY_PLANS_LIST_PAGE_SIZE = 10;
export const FAIR_PAY_EMPLOYEES_LIST_PAGE_SIZE = 25;

function appendPayPlansListParam(query, key, value) {
  if (value == null) return;
  const trimmed = String(value).trim();
  if (!trimmed) return;
  query[key] = trimmed;
}

function appendRepeatListParam(query, key, value) {
  if (Array.isArray(value)) {
    const next = value.map((item) => String(item).trim()).filter(Boolean);
    if (next.length) query[key] = next;
    return;
  }
  appendPayPlansListParam(query, key, value);
}

/**
 * Fair Pay → Pay Plans list (ER-10860 / ER-10862).
 * Default page size is 10. Search sends employee_name and
 * primary_position_name as separate OR'd params.
 */
export const listEmployeePayPlans = async (account_id, params = {}) => {
  const pageSize = Math.max(1, Number(params.pageSize || params.limit) || PAY_PLANS_LIST_PAGE_SIZE);
  const page = Math.max(1, Number(params.page) || 1);
  const offset =
    params.offset != null ? Math.max(0, Number(params.offset) || 0) : (page - 1) * pageSize;

  const query = { limit: pageSize, offset };
  appendPayPlansListParam(query, "employee_name", params.employee_name);
  appendPayPlansListParam(query, "primary_position_name", params.primary_position_name);
  appendPayPlansListParam(query, "fair_pay_status", params.fair_pay_status);
  appendPayPlansListParam(query, "pay_plan", params.pay_plan);
  appendPayPlansListParam(query, "changes", params.changes);
  appendPayPlansListParam(query, "department", params.department);
  appendPayPlansListParam(query, "department_scope", params.department_scope);
  appendPayPlansListParam(query, "as_of_date", params.as_of_date);

  const requestConfig = {
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${API.EMPLOYEE_PAY_PLANS_LIST}`,
    params: query,
  };
  if (params.signal != null) {
    requestConfig.signal = params.signal;
  }

  return axios()(requestConfig);
};

/**
 * Fair Pay → Employee Pay Plans page (ER-12065).
 * Fetches employee header data, primary position assignments,
 * pay range subscriptions with versions, and all pay plan versions.
 */
export const getEmployeePayPlanSummary = async (account_id, employee_id) => {
  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${employee_id}/${API.EMPLOYEE_PAY_PLAN_SUMMARY}`,
  });
};

/**
 * Fair Pay → Employees list (Base Pay tab).
 * Default page size is 25. Filters are AND across groups, OR within a group.
 * `as_of_date` re-resolves every row (Rule 6).
 */
export const listFairPayEmployees = async (account_id, params = {}) => {
  const pageSize = Math.max(
    1,
    Number(params.pageSize || params.limit) || FAIR_PAY_EMPLOYEES_LIST_PAGE_SIZE
  );
  const page = Math.max(1, Number(params.page) || 1);
  const offset =
    params.offset != null ? Math.max(0, Number(params.offset) || 0) : (page - 1) * pageSize;

  const query = { limit: pageSize, offset };
  appendPayPlansListParam(query, "as_of_date", params.as_of_date);
  appendPayPlansListParam(query, "employee_name", params.employee_name);
  appendPayPlansListParam(query, "department_scope", params.department_scope);
  appendRepeatListParam(query, "department", params.department);
  appendRepeatListParam(query, "position", params.position);
  appendRepeatListParam(query, "verdict", params.verdict);
  appendRepeatListParam(query, "review_status", params.review_status);
  if (params.show_reference_currency === true || params.show_reference_currency === "true") {
    query.show_reference_currency = true;
  }

  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${API.EMPLOYEE_FAIR_PAY_LIST}`,
    params: query,
    paramsSerializer: repeatQueryParamsSerializer,
  });
};

/**
 * CONTRACT_DIRECT create/commit (ER-11100).
 * POST .../employee/{id}/commit-pay-plan/
 */
export const commitEmployeePayPlan = async (account_id, employee_id, payload) => {
  return axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${employee_id}/${API.EMPLOYEE_COMMIT_PAY_PLAN}`,
    data: payload,
  });
};

/**
 * Create/update the DRAFT first base pay plan (ER-11966).
 * PUT .../employee/{id}/pay-plan-draft/
 */
export const saveEmployeePayPlanDraft = async (account_id, employee_id, payload) => {
  return axios()({
    method: "put",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${employee_id}/${API.EMPLOYEE_PAY_PLAN_DRAFT}`,
    data: payload,
  });
};

/**
 * Create a DRAFT pay-plan proposal (ER-11160).
 * POST .../account/{id}/pay-plan-proposals/
 */
export const createPayPlanProposal = async (account_id, payload) => {
  return axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/${API.PAY_PLAN_PROPOSALS}`,
    data: payload,
  });
};

/** Account-scoped base for the PayPlanProposal v2 viewset (ER-10473 / ER-11160). */
function payPlanProposalUrl(account_id, proposal_id = "") {
  return `${API.ACCOUNT_LIST_V2}${account_id}/${API.PAY_PLAN_PROPOSALS}${proposal_id}`;
}

/**
 * Fetch a single pay-plan proposal.
 * GET .../account/{id}/pay-plan-proposals/{proposal_id}/
 *
 * NOTE: the read endpoint and rich read serializer (proposed terms, approvers,
 * resulting version) are owned by ER-12107. This function targets the
 * contract that ticket will provide and returns the thin serializer in the
 * interim.
 */
export const getPayPlanProposal = async (account_id, proposal_id) => {
  return axios()({
    method: "get",
    url: `${payPlanProposalUrl(account_id, proposal_id)}/`,
  });
};

/**
 * Hard-delete a DRAFT proposal (Rule B-19).
 * DELETE .../account/{id}/pay-plan-proposals/{proposal_id}/
 * Backend endpoint owned by ER-12107.
 */
export const deletePayPlanProposal = async (account_id, proposal_id) => {
  return axios()({
    method: "delete",
    url: `${payPlanProposalUrl(account_id, proposal_id)}/`,
  });
};

/**
 * Submit a DRAFT proposal for approval (Rule B-4/B-9).
 * POST .../account/{id}/pay-plan-proposals/{proposal_id}/submit-for-approval/
 */
export const submitPayPlanProposal = async (account_id, proposal_id) => {
  return axios()({
    method: "post",
    url: `${payPlanProposalUrl(account_id, proposal_id)}/submit-for-approval/`,
  });
};

/**
 * Reset a PENDING / PENDING_EXECUTION proposal back to DRAFT (Rule B-6).
 * POST .../pay-plan-proposals/{proposal_id}/reset-to-draft/
 * `notify_approvers` mirrors the dialog's unchecked-by-default toggle (B-16).
 */
export const resetPayPlanProposalToDraft = async (
  account_id,
  proposal_id,
  { notify_approvers = false } = {}
) => {
  return axios()({
    method: "post",
    url: `${payPlanProposalUrl(account_id, proposal_id)}/reset-to-draft/`,
    data: { notify_approvers },
  });
};

/**
 * Execute an APPROVED proposal → PENDING_EXECUTION (Rule B-11).
 * POST .../pay-plan-proposals/{proposal_id}/execute/
 */
export const executePayPlanProposal = async (account_id, proposal_id) => {
  return axios()({
    method: "post",
    url: `${payPlanProposalUrl(account_id, proposal_id)}/execute/`,
  });
};

/**
 * Reconcile a PENDING_EXECUTION proposal → RECONCILED (Rule B-12).
 * POST .../pay-plan-proposals/{proposal_id}/reconcile/
 * `payroll_verified` is the required checkbox gate; `notify_employee`
 * mirrors the dialog's checked-by-default toggle (B-16).
 */
export const reconcilePayPlanProposal = async (
  account_id,
  proposal_id,
  { payroll_verified, notify_employee = true }
) => {
  return axios()({
    method: "post",
    url: `${payPlanProposalUrl(account_id, proposal_id)}/reconcile/`,
    data: { payroll_verified, notify_employee },
  });
};

/**
 * Void a ruled-on (Approved / Rejected) proposal with a required reason
 * (Rule B-19, user-facing action "Void").
 * POST .../pay-plan-proposals/{proposal_id}/void/
 */
export const voidPayPlanProposal = async (account_id, proposal_id, { reason }) => {
  return axios()({
    method: "post",
    url: `${payPlanProposalUrl(account_id, proposal_id)}/void/`,
    data: { reason },
  });
};

/**
 * Add an approver to a DRAFT proposal (Rule B-7a).
 * POST .../pay-plan-proposals/{proposal_id}/add-approver/
 * The action names the approver via `approver_employee_id` (employee id in the
 * same account) — the viewset resolves it to an Access.
 */
export const addPayPlanProposalApprover = async (account_id, proposal_id, { employee_id }) => {
  return axios()({
    method: "post",
    url: `${payPlanProposalUrl(account_id, proposal_id)}/add-approver/`,
    data: { approver_employee_id: employee_id },
  });
};

/**
 * Remove an approver from a DRAFT proposal (Rule B-7a).
 * POST .../pay-plan-proposals/{proposal_id}/remove-approver/
 *
 * The version detail (ER-12345) keys its `approvers` on the **Access id**
 * (`task.assignee_id`), so pass `access_id` for rows loaded from the detail and
 * `employee_id` for one just picked from the employee list. The viewset accepts
 * either (`approver_id` / `approver_employee_id`).
 */
export const removePayPlanProposalApprover = async (
  account_id,
  proposal_id,
  { employee_id, access_id } = {}
) => {
  const data =
    access_id != null ? { approver_id: access_id } : { approver_employee_id: employee_id };
  return axios()({
    method: "post",
    url: `${payPlanProposalUrl(account_id, proposal_id)}/remove-approver/`,
    data,
  });
};

/**
 * Wizard Step 1 context for one (employee, effective_date) — ER-11961.
 * GET .../account/{id}/employee/{employee_id}/pay-plan-context/
 * Returns the scenario, pay range, defaults, and `readiness_module_enabled`
 * (mapped to the drawer's readinessEnabled flag).
 */
export const getPayPlanContext = async (
  account_id,
  employee_id,
  { effective_date, amount } = {}
) => {
  const params = {};
  if (effective_date) params.effective_date = effective_date;
  if (amount != null) params.amount = amount;
  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${employee_id}/${API.PAY_PLAN_CONTEXT}`,
    params,
  });
};

/** Account-scoped base for the committed pay-plan-version v2 viewset (ER-12115). */
function payPlanVersionUrl(account_id, version_id = "") {
  return `${API.ACCOUNT_LIST_V2}${account_id}/${API.PAY_PLAN_VERSIONS}${version_id}`;
}

/**
 * Fetch one committed pay-plan version's detail (ER-12115).
 * GET .../account/{id}/pay-plan-versions/{version_id}/
 * Returns the terms, "Fair Pay at effective date" block and the
 * voidable / void_blocked_by / terminable flags (Rule A-8/A-9).
 */
export const getPayPlanVersionDetailV2 = async (account_id, version_id) => {
  return axios()({
    method: "get",
    url: `${payPlanVersionUrl(account_id, version_id)}/`,
  });
};

/**
 * Void a committed pay-plan version (Rule A-8/A-9) — ER-12113.
 * POST .../account/{id}/pay-plan-versions/{version_id}/void/
 *
 * NOTE: the backend viewset (pay-plan-versions) is read-only today; the void
 * action endpoint is owned by a separate backend ticket and 404s until it
 * lands. This function targets that contract so the frontend is ready.
 */
export const voidPayPlanVersionV2 = async (account_id, version_id, { reason }) => {
  return axios()({
    method: "post",
    url: `${payPlanVersionUrl(account_id, version_id)}/void/`,
    data: { reason },
  });
};

/**
 * Terminate an In Force pay-plan version (Rule A-7) — ER-12118.
 * POST .../account/{id}/pay-plan-versions/{version_id}/terminate/
 *
 * NOTE: mirrors voidPayPlanVersionV2 — the committed-version lifecycle actions
 * are owned by a separate backend ticket and 404 until it lands. This function
 * targets that contract so the Current drawer is ready.
 */
export const terminatePayPlanVersionV2 = async (
  account_id,
  version_id,
  { termination_date = null, reason } = {}
) => {
  return axios()({
    method: "post",
    url: `${payPlanVersionUrl(account_id, version_id)}/terminate/`,
    data: { termination_date, reason },
  });
};

export const getMentionableEmployees = async ({ account_id, query = "", signal } = {}) => {
  const result = await axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/${API.EMPLOYEES_MENTIONABLE}`,
    params: { q: query },
    signal,
  });
  const rows = Array.isArray(result.data) ? result.data : result.data?.results;
  return (Array.isArray(rows) ? rows : []).filter((row) => row?.employee_id != null);
};
